import { useState, useMemo, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import Spinner from '../../../components/common/Spinner';
import styled from 'styled-components';
import {
  IoSearchOutline,
  IoCloseOutline,
  IoCallOutline,
  IoChatboxOutline,
  IoCopyOutline,
} from 'react-icons/io5';
import {
  FaTruck,
  FaBroom,
  FaWifi,
  FaPaintRoller,
  FaSnowflake,
  FaGlassWater,
  FaDesktop,
} from 'react-icons/fa6';
import { PARTNER_SIGNUP_CATEGORIES } from '../../../config/ieumConfig';
import { useAuth } from '../../../context/AuthContext';
import { getMyCustomers } from '../../../services/partnerDashboardService';
import {
  MOVING_TYPE_LABELS,
  CLEANING_TYPES,
  INTERNET_SERVICE_TYPES,
  INTERIOR_STYLE_LABELS,
} from '../../../services/customerFormService';
import { updateAssignmentStatus } from '../../../services/partnerAssignmentService';
import { doc, updateDoc, getDoc, addDoc, collection, serverTimestamp, arrayUnion } from 'firebase/firestore';
import { db } from '../../../firebase/config';

const CLEANING_TYPE_LABELS = CLEANING_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: t.label }), {});
const INTERNET_TYPE_LABELS = INTERNET_SERVICE_TYPES.reduce((acc, t) => ({ ...acc, [t.key]: t.label }), {});

// ───── 상수 ─────
// UI 통합 상태 (unread/read/pre_consult/in_progress → 상담전)
const normalizeStatus = (raw) => {
  if (!raw) return 'pre_consult';
  if (raw === 'unread' || raw === 'read' || raw === 'in_progress') return 'pre_consult';
  return raw;
};

const UI_STATUS_LABEL = {
  pre_consult: '상담전',
  consulting: '상담중',
  absent: '부재중',
  visiting: '방문상담',
  reserved: '계약완료',
  completed: '전체완료',
  cancelled: '취소',
};

export const getPartnerStatusLabel = (raw) => UI_STATUS_LABEL[normalizeStatus(raw)] || raw || '-';

const PIPELINE = [
  { key: 'pre_consult', label: '상담전' },
  { key: 'consulting', label: '상담중' },
  { key: 'absent', label: '부재중' },
  { key: 'visiting', label: '방문상담' },
  { key: 'reserved', label: '계약완료' },
];

const SOURCE_FILTERS = [
  { value: 'all', label: '전체' },
  { value: 'assigned', label: '배정 DB' },
  { value: 'purchased', label: '구매 DB' },
];

const STATUS_BADGE = {
  pre_consult: { label: '상담전', bg: '#E0E7FF', color: '#4338CA' },
  consulting: { label: '상담중', bg: '#EDE9FE', color: '#7C3AED' },
  absent: { label: '부재중', bg: '#FED7AA', color: '#EA580C' },
  visiting: { label: '방문상담', bg: '#FEF3C7', color: '#D97706' },
  reserved: { label: '계약완료', bg: '#D1FAE5', color: '#059669' },
  completed: { label: '전체완료', bg: '#D1FAE5', color: '#059669' },
  cancelled: { label: '취소', bg: '#E5E7EB', color: '#6B7280' },
};

const getStatusBadge = (raw) => STATUS_BADGE[normalizeStatus(raw)] || STATUS_BADGE.pre_consult;

// 기능 버튼 (취소 제거, 상태변경·메모 중심)
const STATUS_QUICK_ACTIONS = {
  pre_consult: ['absent', 'change', 'memo'],
  consulting: ['absent', 'change', 'memo'],
  visiting: ['reserved', 'absent', 'change', 'memo'],
  absent: ['change', 'memo'],
  reserved: ['completed', 'absent', 'change', 'memo'],
};

const ACTION_LABEL = {
  completed: '전체완료',
  reserved: '계약완료',
  visiting: '방문상담',
  absent: '부재중',
  change: '상태변경',
  memo: '메모',
};

const CATEGORY_ICON_MAP = {
  moving: FaTruck,
  cleaning: FaBroom,
  internet_tv: FaWifi,
  interior: FaPaintRoller,
  aircon: FaSnowflake,
  water_purifier: FaGlassWater,
  kiosk: FaDesktop,
};

// 평수 기반 가격 카테고리 (per_pyeong) — 정책: 이사·청소만 평당, 인테리어/인터넷TV는 고정
const PER_PYEONG_CATEGORIES = ['moving', 'cleaning'];

const AREA_LABEL = {
  under_12: '12평 이하',
  between_12_20: '12~20평',
  over_20: '20평 이상',
};

const fmt = (n) => `₩${(n || 0).toLocaleString()}`;

// ───── 컴포넌트 ─────
const COMPLETED_STATUSES = ['completed', 'cancelled'];

const MyCustomerTab = ({ mode = 'active' }) => {
  const isCompleted = mode === 'completed';
  const { userData } = useAuth();
  const [searchParams] = useSearchParams();
  const initialStatus = searchParams.get('status') || '';
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(initialStatus);
  const [statusModal, setStatusModal] = useState(null); // { customer, targetStatus }
  const [memoModal, setMemoModal] = useState(null);
  const [infoModal, setInfoModal] = useState(null); // 'commission' | 'policy'

  const load = useCallback(async () => {
    if (!userData?.uid) return;
    setLoading(true);
    try {
      const cats = userData?.service_categories || userData?.serviceCategories || null;
      const list = await getMyCustomers(userData.uid, cats);
      // 내 고객 탭 진입 시 미열람(pre_consult) → 열람(in_progress) 자동 전환
      if (!isCompleted) {
        const toMark = list.filter((c) => c.isNew && c.status === 'pre_consult' && c.assignmentId);
        if (toMark.length > 0) {
          await Promise.all(
            toMark.map((c) =>
              updateDoc(doc(db, 'partner_assignments', c.assignmentId), {
                status: 'in_progress',
                read_at: serverTimestamp(),
                updated_at: serverTimestamp(),
              }).catch(() => null)
            )
          );
          toMark.forEach((c) => { c.status = 'in_progress'; c.isNew = false; });
        }
      }
      const filtered = isCompleted
        ? list.filter((c) => COMPLETED_STATUSES.includes(normalizeStatus(c.status)))
        : list.filter((c) => !COMPLETED_STATUSES.includes(normalizeStatus(c.status)));
      setCustomers(filtered);
    } catch (err) {
      console.error('my customers failed', err);
    } finally {
      setLoading(false);
    }
  }, [userData?.uid, isCompleted]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    return customers.filter((c) => {
      if (search && !c.name.includes(search) && !c.phone.includes(search)) return false;
      if (sourceFilter !== 'all' && c.source !== sourceFilter) return false;
      if (statusFilter && normalizeStatus(c.status) !== statusFilter) return false;
      return true;
    });
  }, [customers, search, sourceFilter, statusFilter]);

  const handleQuickAction = (customer, action) => {
    if (action === 'change') {
      setStatusModal({ customer, targetStatus: null });
    } else if (action === 'memo') {
      setMemoModal({ customer });
    } else {
      setStatusModal({ customer, targetStatus: action });
    }
  };

  const pipelineCounts = useMemo(() => {
    const counts = {};
    PIPELINE.forEach((p) => { counts[p.key] = 0; });
    customers.forEach((c) => {
      const key = normalizeStatus(c.status);
      if (counts[key] !== undefined) counts[key] += 1;
    });
    return counts;
  }, [customers]);

  const completedSummary = useMemo(() => {
    const counts = { completed: 0, cancelled: 0 };
    customers.forEach((c) => {
      const key = normalizeStatus(c.status);
      if (counts[key] !== undefined) counts[key] += 1;
    });
    return counts;
  }, [customers]);

  if (loading) return <Spinner fullPage={false} />;

  return (
    <>
      {/* 상담 현황 파이프라인 (활성 탭에서만 노출) */}
      {!isCompleted && (
        <PipelineSection>
          <PipelineTitle>상담 현황</PipelineTitle>
          <PipelineScroll>
            {PIPELINE.flatMap((p, i) => {
              const chip = (
                <PipelineChip
                  key={p.key}
                  type="button"
                  $active={statusFilter === p.key}
                  onClick={() => setStatusFilter(statusFilter === p.key ? '' : p.key)}
                >
                  <ChipLabel>{p.label}</ChipLabel>
                  <ChipCount>{pipelineCounts[p.key] || 0}</ChipCount>
                </PipelineChip>
              );
              return i < PIPELINE.length - 1
                ? [chip, <PipelineArrow key={`${p.key}-arrow`}>›</PipelineArrow>]
                : [chip];
            })}
          </PipelineScroll>
          <PipelineDesc>필터는 현재(최근) 상태 기준 · 항목 클릭 시 해당 상태만 표시</PipelineDesc>
        </PipelineSection>
      )}

      {/* 완료 탭: 전체완료 / 취소 현황 2박스 */}
      {isCompleted && (
        <CompletedSummary>
          <SummaryBox
            type="button"
            $active={statusFilter === 'completed'}
            onClick={() => setStatusFilter(statusFilter === 'completed' ? '' : 'completed')}
          >
            <SummaryLabel>전체완료</SummaryLabel>
            <SummaryCount>{completedSummary.completed}건</SummaryCount>
          </SummaryBox>
          <SummaryBox
            type="button"
            $active={statusFilter === 'cancelled'}
            $muted
            onClick={() => setStatusFilter(statusFilter === 'cancelled' ? '' : 'cancelled')}
          >
            <SummaryLabel>취소</SummaryLabel>
            <SummaryCount>{completedSummary.cancelled}건</SummaryCount>
          </SummaryBox>
        </CompletedSummary>
      )}

      {/* 검색 */}
      <SearchBox>
        <IoSearchOutline size={18} color="#9CA3AF" />
        <SearchInput
          type="text"
          placeholder="고객 명단 검색"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </SearchBox>

      {/* 소스 필터 - 드롭다운 (완료 탭에서는 숨김) */}
      {!isCompleted && (
        <SourceSelect
          value={sourceFilter}
          onChange={(e) => setSourceFilter(e.target.value)}
        >
          {SOURCE_FILTERS.map((f) => (
            <option key={f.value} value={f.value}>{f.label}</option>
          ))}
        </SourceSelect>
      )}

      <ListSectionTitle>
        총 {filtered.length}건
        {!isCompleted && (
          <PolicyLink type="button" onClick={() => setInfoModal('policy')}>영업 및 환불 정책</PolicyLink>
        )}
      </ListSectionTitle>

      {/* 카드 리스트 */}
      <CardList>
        {filtered.map((c) => (
          <CustomerCard
            key={c.id}
            data={c}
            onAction={(action) => handleQuickAction(c, action)}
            onFeeInfo={() => setInfoModal({ type: 'commission', category: c.category })}
            isCompleted={isCompleted}
          />
        ))}
      </CardList>

      {filtered.length === 0 && (
        <EmptyCard>
          <EmptyInner>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
              <polyline points="14 2 14 8 20 8"/>
              <line x1="4" y1="3" x2="20" y2="21"/>
            </svg>
            <EmptyTitle>조건에 맞는 DB가 없습니다.</EmptyTitle>
            <EmptySub>필터를 변경하거나 나중에 다시 확인해보세요.</EmptySub>
          </EmptyInner>
        </EmptyCard>
      )}

      {/* 안내 모달 */}
      {infoModal && (
        <InfoModalBox
          type={typeof infoModal === 'string' ? infoModal : infoModal.type}
          category={typeof infoModal === 'object' ? infoModal.category : null}
          onClose={() => setInfoModal(null)}
        />
      )}

      {/* 상태 변경 모달 */}
      {statusModal && (
        <StatusChangeModal
          customer={statusModal.customer}
          targetStatus={statusModal.targetStatus}
          onClose={() => setStatusModal(null)}
          onSaved={() => { setStatusModal(null); load(); }}
        />
      )}

      {/* 메모 모달 */}
      {memoModal && (
        <MemoModal
          customer={memoModal.customer}
          onClose={() => setMemoModal(null)}
          onSaved={() => { setMemoModal(null); load(); }}
        />
      )}
    </>
  );
};

export default MyCustomerTab;

// ───── 영업 수수료 / 정책 모달 ─────
const COMMISSION_TABS = [
  {
    key: 'moving',
    label: '이사',
    summary: '12평 기준 산출액: 포장이사 (평당 4,000원) = 48,000원',
    rows: [
      { name: '일반이사', fee: 1500 },
      { name: '반포장이사', fee: 3000 },
      { name: '포장이사', fee: 4000 },
      { name: '보관이사', fee: 4000 },
    ],
  },
  {
    key: 'cleaning',
    label: '청소',
    summary: '12평 기준 산출액: 입주청소 (평당 3,000원) = 36,000원',
    rows: [
      { name: '일반청소', fee: 2000 },
      { name: '입주청소', fee: 3000 },
      { name: '특수청소', fee: 4500 },
    ],
  },
  {
    key: 'internet',
    label: '인터넷',
    summary: '설치 전용 산출액: 인터넷 단독 설치 = 15,000원',
    rows: [
      { name: '인터넷 단독', fee: 15000 },
      { name: 'TV + 인터넷', fee: 20000 },
      { name: '결합상품', fee: 25000 },
    ],
    perInstall: true,
  },
  {
    key: 'interior',
    label: '인테리어',
    summary: '12평 기준 산출액: 도배 (평당 5,000원) = 60,000원',
    rows: [
      { name: '도배', fee: 5000 },
      { name: '장판', fee: 4000 },
      { name: '도배+장판', fee: 8000 },
    ],
  },
];

const POLICY_TEXT = `영업 및 환불 정책

1. 영업 활동은 해당 공인중개사에서 배정한 고객을 대상으로만 진행합니다.
2. 구매한 DB는 30일 이내 상담이 이루어지지 않을 경우 자동 환불 대상이 됩니다.
3. 환불 신청은 마이페이지 > 문의 경로로 접수해 주세요.
4. 고의적 연락 지연, 허위 상담, 중복 청구 등 부정 행위가 확인될 경우 계정이 정지될 수 있습니다.
5. 본 정책은 사전 고지 없이 개정될 수 있으며, 변경 시 앱 내 공지로 안내합니다.`;

const InfoModalBox = ({ type, category, onClose }) => {
  const { userData } = useAuth();
  // 로그인 업체의 서비스 카테고리만 노출 (없으면 전체)
  const myCats = (userData?.service_categories || []).map((c) =>
    c === 'internet_tv' ? 'internet' : c
  );
  const visibleTabs = myCats.length > 0
    ? COMMISSION_TABS.filter((t) => myCats.includes(t.key))
    : COMMISSION_TABS;
  const tabsToShow = visibleTabs.length > 0 ? visibleTabs : COMMISSION_TABS;

  // 카테고리 매핑 (internet_tv → internet)
  const initialKey = category === 'internet_tv' ? 'internet' : (category || tabsToShow[0]?.key || 'moving');
  const [tab, setTab] = useState(
    tabsToShow.find((t) => t.key === initialKey) ? initialKey : tabsToShow[0]?.key || 'moving'
  );
  const current = tabsToShow.find((t) => t.key === tab) || tabsToShow[0];

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>{type === 'policy' ? '영업 및 환불 정책' : '영업 수수료 정산 안내'}</ModalTitle>
          <ModalClose type="button" onClick={onClose}>
            <IoCloseOutline size={22} />
          </ModalClose>
        </ModalHeader>

        {type === 'policy' ? (
          <PolicyContent>{POLICY_TEXT}</PolicyContent>
        ) : (
          <>
            {tabsToShow.length > 1 && (
              <InfoTabRow>
                {tabsToShow.map((t) => (
                  <InfoTab key={t.key} type="button" $active={tab === t.key} onClick={() => setTab(t.key)}>
                    {t.label}
                  </InfoTab>
                ))}
              </InfoTabRow>
            )}
            <InfoSummaryBox>
              예약이 완료된 후 기준으로 영업수수료가 발생하며, 해당 금액은 시스템 내 마이페이지에서 확인 가능합니다.
              아래 금액은 예시이며 실제 서비스 단가에 따라 변동됩니다.
            </InfoSummaryBox>
            <InfoHighlight>{current.summary}</InfoHighlight>
            <InfoTable>
              <thead>
                <tr>
                  <InfoTh>서비스 명</InfoTh>
                  <InfoTh $right>{current.perInstall ? '건당 수수료(원)' : '면적당 수수료(원)'}</InfoTh>
                </tr>
              </thead>
              <tbody>
                {current.rows.map((r) => (
                  <tr key={r.name}>
                    <InfoTd>{r.name}</InfoTd>
                    <InfoTd $right>{r.fee.toLocaleString()}</InfoTd>
                  </tr>
                ))}
              </tbody>
            </InfoTable>
            <InfoFootnote>※ 실제 수수료는 서비스 유형 및 면적에 따라 변동될 수 있습니다.</InfoFootnote>
          </>
        )}
      </ModalBox>
    </ModalOverlay>
  );
};

// ───── 카드 ─────
const CustomerCard = ({ data, onAction, onFeeInfo, isCompleted }) => {
  const cat = PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === data.category);
  const uiStatus = normalizeStatus(data.status);
  const badge = getStatusBadge(data.status);
  const actions = STATUS_QUICK_ACTIONS[uiStatus] || ['change', 'memo'];
  const CategoryIcon = CATEGORY_ICON_MAP[data.category];

  const phoneDigits = (data.phone || '').replace(/-/g, '');
  // 계약 전: 이사 일자 (data.movingDate)
  // 계약 후(reserved/completed): 예정 일자 (data.reservationDate || data.movingDate)
  const isContracted = uiStatus === 'reserved' || uiStatus === 'completed';
  const dateLabelText = isContracted ? '예정 일자' : '이사 일자';
  const movingDateDisplay = isContracted
    ? (data.reservationDate || data.movingDate || '')
    : (data.movingDate || '');
  const addressDisplay = data.address || data.movingAddress || data.moving_address || '';
  const isPerPyeong = data.priceType
    ? data.priceType === 'per_pyeong'
    : PER_PYEONG_CATEGORIES.includes(data.category);
  // 평당 카테고리는 평수 무조건 표시: 실평수 우선, 없으면 구간 라벨 fallback
  const areaLabel = data.areaPyeongExact
    ? `${data.areaPyeongExact}평`
    : (AREA_LABEL[data.areaSize] || '');
  // 상품명: 카테고리별 세부 종류 (이사 종류/청소 종류/인터넷 종류/인테리어 스타일)
  const productLabel = data.category === 'moving'
    ? (MOVING_TYPE_LABELS[data.movingType] || '')
    : data.category === 'cleaning'
    ? (CLEANING_TYPE_LABELS[data.cleaningType] || '')
    : data.category === 'internet_tv'
    ? (INTERNET_TYPE_LABELS[data.internetType] || '')
    : data.category === 'interior'
    ? (INTERIOR_STYLE_LABELS[data.interiorStyle] || '')
    : '';

  return (
    <Card>
      <CardTopRow>
        <CardCategory>
          {CategoryIcon && <CategoryIcon size={14} />}
          <span>
            {cat?.label || data.category}
            {productLabel ? ` (${productLabel})` : ''}
          </span>
        </CardCategory>
        <CardStatusPill $bg={badge.bg} $color={badge.color}>
          {badge.label}
        </CardStatusPill>
      </CardTopRow>

      <NameRow>
        <Name>{data.name}</Name>
        <SourceTag>{data.sourceLabel || (data.source === 'purchased' ? 'DB 마켓' : '배정 DB')}</SourceTag>
        {data.isNew && <NewBadge>NEW</NewBadge>}
      </NameRow>

      {movingDateDisplay && (
        <MovingDateRow>
          <MovingDateLabel>{dateLabelText}</MovingDateLabel>
          <MovingDateValue>{movingDateDisplay}</MovingDateValue>
        </MovingDateRow>
      )}

      {addressDisplay && (
        <MovingDateRow>
          <MovingDateLabel>주소지</MovingDateLabel>
          <MovingDateValue>{addressDisplay}</MovingDateValue>
        </MovingDateRow>
      )}

      {isPerPyeong && (
        <MetaRow>
          <MetaLabel>평수</MetaLabel>
          <MetaValue>{areaLabel || '-'}</MetaValue>
        </MetaRow>
      )}

      <CardBodyRow>
        <CardBodyLeft>
          {(data.reservationDate || data.purchasePrice > 0) ? (
            <>
              {data.reservationDate && movingDateDisplay !== data.reservationDate && (
                <ReservedLine>
                  <ReservedDot />
                  <ReservedLabel>예약일</ReservedLabel>
                  <ReservedValue>{data.reservationDate}</ReservedValue>
                </ReservedLine>
              )}
              {data.purchasePrice > 0 && (
                <AmountLine>{fmt(data.purchasePrice)}</AmountLine>
              )}
            </>
          ) : (
            <BodyPlaceholder>진행 정보가 아직 없습니다.</BodyPlaceholder>
          )}
        </CardBodyLeft>
        <CardBodyRight>
          <MemoBoxInline>
            <MemoBoxTitle><IoChatboxOutline size={12} /> 최근 메모</MemoBoxTitle>
            <MemoBoxText>{
              data.memo
              || (Array.isArray(data.memoHistory) && data.memoHistory.length > 0
                  ? data.memoHistory[data.memoHistory.length - 1]?.memo
                  : '')
              || '불편 사항 메모가 여기에 표시됩니다.'
            }</MemoBoxText>
          </MemoBoxInline>
        </CardBodyRight>
      </CardBodyRow>

      {data.phone && (
        <CardPhoneRow href={`tel:${phoneDigits}`}>
          <IoCallOutline size={18} />
          <CardPhoneText>{data.phone}</CardPhoneText>
        </CardPhoneRow>
      )}

      {isCompleted ? (
        <>
          <CompletedRow>
            <CompletedLabel>완료일시</CompletedLabel>
            <CompletedValue>{data.completedAt || data.updatedAt || data.date || '-'}</CompletedValue>
          </CompletedRow>
          <ActionGrid $cols={1}>
            <ActionBtn type="button" onClick={() => onAction('memo')}>
              {ACTION_LABEL.memo}
            </ActionBtn>
          </ActionGrid>
        </>
      ) : (
        <>
          {actions.includes('completed') && (
            <CompleteCtaBtn type="button" onClick={() => onAction('completed')}>
              {ACTION_LABEL.completed}
            </CompleteCtaBtn>
          )}
          <ActionGrid $cols={2}>
            {actions.filter((a) => a !== 'completed').map((act) => (
              <ActionBtn
                key={act}
                type="button"
                onClick={() => onAction(act)}
              >
                {ACTION_LABEL[act] || act}
              </ActionBtn>
            ))}
          </ActionGrid>
        </>
      )}

      {!isCompleted && onFeeInfo && (
        <CardFeeLink type="button" onClick={onFeeInfo}>
          {cat?.label || '서비스'} 영업 수수료 안내
        </CardFeeLink>
      )}
    </Card>
  );
};

// ───── 상태 변경 모달 ─────
const TARGET_STATUSES = [
  { value: 'consulting', label: '상담중', color: '#8B5CF6' },
  { value: 'visiting', label: '방문상담', color: '#F59E0B' },
  { value: 'reserved', label: '계약완료', color: '#22C55E' },
  { value: 'absent', label: '부재중', color: '#EA580C' },
  { value: 'completed', label: '전체완료', color: '#059669' },
];

const CANCEL_REASONS = [
  '고객 변심',
  '업체 사정',
  '기타',
];

const StatusChangeModal = ({ customer, targetStatus, onClose, onSaved }) => {
  const currentUi = normalizeStatus(customer.status);
  const [status, setStatus] = useState(targetStatus || (currentUi === 'reserved' ? 'reserved' : 'consulting'));
  const [reservedDate, setReservedDate] = useState(customer.reservationDate || '');
  // 진행금액 / 사은품: 이미 계약완료 상태에서 수정 시에만 기존값 prefill, 첫 입력은 항상 빈칸
  const isAlreadyReserved = currentUi === 'reserved';
  const [amount, setAmount] = useState(isAlreadyReserved && customer.price ? String(customer.price) : '');
  const [supportAmount, setSupportAmount] = useState(isAlreadyReserved && customer.supportAmount ? String(customer.supportAmount) : '');
  const [visitDate, setVisitDate] = useState('');
  const [memo, setMemo] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [saving, setSaving] = useState(false);

  const currentBadge = getStatusBadge(customer.status);

  const isCurrentlyReserved = currentUi === 'reserved';
  const needsReserved = status === 'reserved';
  const needsVisit = status === 'visiting';
  const needsCancelReason = status === 'cancelled';
  const isAbsent = status === 'absent';
  // 계약완료 고객의 가격/사은품 조정 영역 (상태가 reserved로 유지되거나 completed로 넘어가는 경우)
  const showReservedEdit = isCurrentlyReserved && (needsReserved || status === 'completed');

  const canSave = needsCancelReason
    ? !!(cancelReason && memo && memo.trim())
    : needsReserved
      ? !!reservedDate
      : true;

  const handleSubmit = async () => {
    if (!customer.assignmentId || !canSave) return;
    setSaving(true);
    try {
      const extra = { memo: memo || null };
      if (memo && memo.trim()) {
        extra.memo_history = arrayUnion({
          memo: memo.trim(),
          status,
          created_at: new Date().toISOString(),
        });
      }

      // 변경 이력 정확도를 위해 최신 assignment 값 조회
      let prevAmount = Number(customer.price || 0);
      let prevSupport = Number(customer.supportAmount || 0);
      try {
        const snap = await getDoc(doc(db, 'partner_assignments', customer.assignmentId));
        if (snap.exists()) {
          const d = snap.data();
          prevAmount = Number(d.price || 0);
          prevSupport = Number(d.support_amount || 0);
        }
      } catch { /* skip */ }

      // 빈 칸은 0 으로 저장 (이전 값 prefill 안 함)
      const nextAmount = amount === '' ? 0 : Number(amount);
      const nextSupport = supportAmount === '' ? 0 : Number(supportAmount);
      const amountChanged = nextAmount !== prevAmount;
      const supportChanged = nextSupport !== prevSupport;

      if (needsReserved) {
        if (reservedDate) extra.reserved_date = reservedDate;
        extra.price = nextAmount;
        extra.support_amount = nextSupport;
      } else if (showReservedEdit) {
        extra.price = nextAmount;
        extra.support_amount = nextSupport;
      }

      // 계약완료 상태에서 금액/사은품 변동 시 변경 이력 기록
      if (isCurrentlyReserved && (amountChanged || supportChanged)) {
        extra.price_history = arrayUnion({
          before: { price: prevAmount, support_amount: prevSupport },
          after: { price: nextAmount, support_amount: nextSupport },
          reason: memo?.trim() || null,
          changed_at: new Date().toISOString(),
        });

        // service_requests 측에도 변경 반영
        if (customer.serviceRequestId) {
          try {
            await updateDoc(doc(db, 'service_requests', customer.serviceRequestId), {
              price: nextAmount,
              support_amount: nextSupport,
              price_updated_at: serverTimestamp(),
            });
          } catch (e) { /* skip */ }
        }

        // TODO: Solapi 알림톡 연동 전까지 notification_logs에 pending 로그만 남김
        try {
          await addDoc(collection(db, 'notification_logs'), {
            type: 'price_changed',
            channel: 'alimtalk',
            status: 'pending',
            target_customer_id: customer.customerId || null,
            target_phone: customer.phone || null,
            assignment_id: customer.assignmentId,
            payload: {
              before: { price: prevAmount, support_amount: prevSupport },
              after: { price: nextAmount, support_amount: nextSupport },
            },
            created_at: serverTimestamp(),
          });
        } catch (e) { /* skip */ }
      }

      if (needsVisit) extra.visit_date = visitDate;
      if (needsCancelReason) extra.cancel_reason = cancelReason;
      await updateAssignmentStatus(customer.assignmentId, status, extra);
      if (onSaved) onSaved(); else onClose();
    } catch (err) {
      console.error('status update failed', err);
      alert('저장 실패');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>상태 변경</ModalTitle>
          <ModalClose type="button" onClick={onClose}>
            <IoCloseOutline size={22} />
          </ModalClose>
        </ModalHeader>

        <ModalSubTitle>
          {customer.name} ({customer.sourceLabel || (customer.source === 'purchased' ? 'DB 마켓' : '배정 DB')}) / {(PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === customer.category)?.label) || customer.category || '-'}
        </ModalSubTitle>

        <CurrentStatusRow>
          <CurrentLabel>현재:</CurrentLabel>
          <CurrentPill $bg={currentBadge.bg} $color={currentBadge.color}>
            {getPartnerStatusLabel(customer.status)}
          </CurrentPill>
        </CurrentStatusRow>

        <ModalLabel>변경할 상태</ModalLabel>
        <StatusGrid>
          {TARGET_STATUSES.filter((s) => s.value !== 'completed' || currentUi === 'reserved').map((s) => (
            <StatusOptBtn
              key={s.value}
              type="button"
              $active={status === s.value}
              $color={s.color}
              $highlight={s.highlight}
              onClick={() => setStatus(s.value)}
            >
              {s.label}
            </StatusOptBtn>
          ))}
        </StatusGrid>

        <CancelDivider />
        <StatusGrid>
          <StatusOptBtn
            type="button"
            $active={status === 'cancelled'}
            $color="#EF4444"
            onClick={() => setStatus('cancelled')}
          >
            취소
          </StatusOptBtn>
        </StatusGrid>

        {needsVisit && (
          <>
            <ModalLabel>방문상담 일정</ModalLabel>
            <ModalInput
              type="date"
              value={visitDate}
              onChange={(e) => setVisitDate(e.target.value)}
            />
          </>
        )}

        {needsReserved && (
          <>
            <ModalSectionTitle>{isCurrentlyReserved ? '계약 정보 수정' : '계약 상세 정보'}</ModalSectionTitle>
            <ModalLabel>계약일자 *</ModalLabel>
            <ModalInput
              type="date"
              value={reservedDate}
              onChange={(e) => setReservedDate(e.target.value)}
            />
            <ModalLabel>진행 금액(원)</ModalLabel>
            <ModalInput
              type="number"
              placeholder="예: 500000"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <ModalLabel>사은품 / 지원금(있는 경우)</ModalLabel>
            <ModalInput
              type="number"
              placeholder="예: 30000"
              value={supportAmount}
              onChange={(e) => setSupportAmount(e.target.value)}
            />
            {isCurrentlyReserved && (
              <PriceChangeHint>
                변경 사항은 고객에게 알림톡으로 안내될 예정입니다. (전송 연동 시 자동 발송)
              </PriceChangeHint>
            )}
          </>
        )}

        {showReservedEdit && !needsReserved && status === 'completed' && (
          <>
            <ModalSectionTitle>최종 금액 확인</ModalSectionTitle>
            <ModalLabel>진행 금액(원)</ModalLabel>
            <ModalInput
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <ModalLabel>사은품 / 지원금</ModalLabel>
            <ModalInput
              type="number"
              value={supportAmount}
              onChange={(e) => setSupportAmount(e.target.value)}
            />
          </>
        )}

        {isAbsent && (
          <AbsentTemplateEditor customer={customer} />
        )}

        {needsCancelReason && (
          <>
            <ModalLabel>취소 사유 *</ModalLabel>
            <RadioGroup>
              {CANCEL_REASONS.map((r) => (
                <RadioItem key={r}>
                  <input
                    type="radio"
                    name="cancel-reason"
                    value={r}
                    checked={cancelReason === r}
                    onChange={(e) => setCancelReason(e.target.value)}
                  />
                  {r}
                </RadioItem>
              ))}
            </RadioGroup>
          </>
        )}

        <ModalLabel>상태 변경 메모 {needsCancelReason ? '* (취소 시 필수)' : '(선택)'}</ModalLabel>
        <ModalTextarea
          placeholder="추가 메모"
          rows={4}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />

        <ModalBtnRow>
          <ModalBtn type="button" $secondary onClick={onClose}>취소</ModalBtn>
          <ModalBtn type="button" onClick={handleSubmit} disabled={saving || !canSave}>
            {saving ? '저장중...' : '변경 완료'}
          </ModalBtn>
        </ModalBtnRow>
      </ModalBox>
    </ModalOverlay>
  );
};

// ───── 부재중 안내 문자 템플릿 ─────
const LEGACY_ABSENT_DEFAULTS = [
  '안녕하세요, 방금 연락드렸으나 부재중이셨습니다.\n편하신 시간에 회신 부탁드립니다. 감사합니다.',
  '안녕하세요 이음플렛폼\n{{서비스명}} 파트너 {{업체명}} 입니다.\n상담차 연락드렸는데 부재중이셔서 문자 남깁니다.\n편하신 시간 회신 주시거나, 연락 부탁드려요.',
];

const buildAbsentTemplate = (serviceName, businessName) =>
  `안녕하세요 이음플렛폼\n${serviceName || '서비스'} 파트너 ${businessName || '업체'} 입니다.\n상담차 연락드렸는데 부재중이셔서 문자 남깁니다.\n편하신 시간 회신 주시거나, 연락 부탁드려요.`;

const AbsentTemplateEditor = ({ customer }) => {
  const { userData } = useAuth();
  const [template, setTemplate] = useState('');
  const [loaded, setLoaded] = useState(false);

  const serviceName =
    PARTNER_SIGNUP_CATEGORIES.find((c) => c.id === customer.category)?.label || '';
  const businessName = userData?.business_name || '';
  const defaultTemplate = buildAbsentTemplate(serviceName, businessName);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!userData?.uid) return;
      try {
        const snap = await getDoc(doc(db, 'users', userData.uid));
        const saved = snap.exists() ? (snap.data().absent_template || '') : '';
        const useSaved = saved && !LEGACY_ABSENT_DEFAULTS.includes(saved);
        if (alive) {
          setTemplate(useSaved ? saved : defaultTemplate);
          setLoaded(true);
        }
      } catch {
        if (alive) { setTemplate(defaultTemplate); setLoaded(true); }
      }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userData?.uid, customer.category, businessName]);

  const saveTemplate = async () => {
    if (!userData?.uid) return;
    try {
      await updateDoc(doc(db, 'users', userData.uid), {
        absent_template: template,
        updated_at: serverTimestamp(),
      });
    } catch { /* skip */ }
  };

  const phone = (customer.phone || '').replace(/-/g, '');
  const handleSms = async () => {
    await saveTemplate();
    window.location.href = `sms:${phone}?body=${encodeURIComponent(template)}`;
  };
  const handleCopy = async () => {
    await saveTemplate();
    try { await navigator.clipboard.writeText(template); alert('복사되었습니다'); }
    catch { alert('복사 실패'); }
  };

  return (
    <AbsentBlock>
      <AbsentSectionTitle>부재중 안내 문자 템플릿</AbsentSectionTitle>
      <ModalTextarea
        rows={5}
        placeholder="부재중 안내 문자 내용을 입력하세요"
        value={template}
        onChange={(e) => setTemplate(e.target.value)}
        onBlur={saveTemplate}
        disabled={!loaded}
      />
      <AbsentPhone>수신번호: <strong>{customer.phone || '-'}</strong></AbsentPhone>
      <AbsentActions>
        <AbsentSmsBtn type="button" onClick={handleSms}>
          <IoChatboxOutline size={18} /> 문자 앱으로 발송
        </AbsentSmsBtn>
        <AbsentCopyBtn type="button" onClick={handleCopy}>
          <IoCopyOutline size={18} /> 내용 복사
        </AbsentCopyBtn>
      </AbsentActions>
      <AbsentHelpText>
        📱 모바일: "문자 앱으로 발송" → 문자앱이 열리며 내용 자동 입력됩니다.<br />
        💻 PC: 내용 복사 후 문자앱에 붙여넣기하세요.<br />
        ※ 마지막으로 발송한 내용이 자동 저장됩니다.
      </AbsentHelpText>
    </AbsentBlock>
  );
};

// ───── 메모 모달 ─────
const MemoModal = ({ customer, onClose, onSaved }) => {
  const [memo, setMemo] = useState('');
  const [history, setHistory] = useState(customer.memoHistory || []);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!customer.assignmentId) return;
      try {
        const snap = await getDoc(doc(db, 'partner_assignments', customer.assignmentId));
        if (alive && snap.exists()) {
          const h = snap.data().memo_history;
          if (Array.isArray(h)) setHistory(h);
        }
      } catch { /* skip */ }
    })();
    return () => { alive = false; };
  }, [customer.assignmentId]);

  const handleSubmit = async () => {
    if (!customer.assignmentId || !memo.trim()) return;
    setSaving(true);
    try {
      await updateDoc(doc(db, 'partner_assignments', customer.assignmentId), {
        memo: memo.trim(),
        memo_history: arrayUnion({
          memo: memo.trim(),
          status: customer.status || null,
          created_at: new Date().toISOString(),
        }),
        updated_at: serverTimestamp(),
      });
      if (onSaved) onSaved(); else onClose();
    } catch (err) {
      console.error('memo save failed', err);
      alert('저장 실패');
    } finally {
      setSaving(false);
    }
  };

  const sortedHistory = [...history].sort((a, b) =>
    (b.created_at || '').localeCompare(a.created_at || '')
  );

  return (
    <ModalOverlay onClick={onClose}>
      <ModalBox onClick={(e) => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>메모</ModalTitle>
          <ModalClose type="button" onClick={onClose}>
            <IoCloseOutline size={22} />
          </ModalClose>
        </ModalHeader>
        <ModalCustomer>{customer.name} · {customer.phone}</ModalCustomer>

        <ModalLabel>메모 이력 ({sortedHistory.length})</ModalLabel>
        <MemoHistoryBox>
          {sortedHistory.length === 0 ? (
            <MemoHistoryEmpty>이전 메모가 없습니다.</MemoHistoryEmpty>
          ) : (
            sortedHistory.map((h, i) => (
              <MemoHistoryItem key={i}>
                <MemoHistoryMeta>
                  {h.status ? `[${getPartnerStatusLabel(h.status)}] ` : ''}
                  {h.created_at ? new Date(h.created_at).toLocaleString('ko-KR') : ''}
                </MemoHistoryMeta>
                <MemoHistoryText>{h.memo}</MemoHistoryText>
              </MemoHistoryItem>
            ))
          )}
        </MemoHistoryBox>

        <ModalLabel>새 메모</ModalLabel>
        <ModalTextarea
          placeholder="고객 관련 메모를 입력해 주세요"
          rows={4}
          value={memo}
          onChange={(e) => setMemo(e.target.value)}
        />

        <ModalBtnRow>
          <ModalBtn type="button" $secondary onClick={onClose}>취소</ModalBtn>
          <ModalBtn type="button" onClick={handleSubmit} disabled={saving || !memo.trim()}>
            {saving ? '저장중...' : '저장'}
          </ModalBtn>
        </ModalBtnRow>
      </ModalBox>
    </ModalOverlay>
  );
};

// ───── 스타일 ─────
const PipelineSection = styled.div`
  background: #F1F5FF;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 10px 6px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const PipelineDesc = styled.p`
  margin: 10px 0 0;
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const PipelineTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const PipelineScroll = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
  min-width: 0;
  width: 100%;

  @media (min-width: 1024px) {
    justify-content: center;
    gap: 6px;
  }
`;

const PipelineArrow = styled.span`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.colors.textLight};
  font-size: 11px;
  font-weight: 700;
  padding: 0;
  margin: 0 1px;
`;

const PipelineChip = styled.button`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
  padding: 6px 1px;
  background: #fff;
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.borderLight)};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  overflow: hidden;

  @media (min-width: 1024px) {
    flex: 0 0 auto;
    min-width: 84px;
    max-width: 100px;
    padding: 10px 4px;
  }
`;

const ChipLabel = styled.span`
  font-size: 9px;
  color: ${({ theme }) => theme.colors.textSecondary};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  max-width: 100%;
`;

const ChipCount = styled.span`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.extrabold};
  color: ${({ theme }) => theme.colors.primary};
`;

const SearchBox = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: 12px 14px;
  background: ${({ theme }) => theme.colors.bgWhite};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const SearchInput = styled.input`
  flex: 1;
  border: 0;
  outline: none;
  font-size: ${({ theme }) => theme.fontSize.sm};
  background: transparent;
  color: ${({ theme }) => theme.colors.text};

  &::placeholder { color: ${({ theme }) => theme.colors.textLight}; }
`;

const SourceSelect = styled.select`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: #fff;
  font-size: ${({ theme }) => theme.fontSize.sm};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ListSectionTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md};
`;

const Card = styled.div`
  background: ${({ theme }) => theme.colors.bgWhite};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 16px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const CardTopRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`;

const CardCategory = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};

  svg {
    color: ${({ theme }) => theme.colors.textSecondary};
  }
`;

const CardStatusPill = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 8px 18px;
  background: ${({ $bg }) => $bg || '#E5E7EB'};
  color: ${({ $color }) => $color || '#6B7280'};
  border-radius: 8px;
  font-size: 15px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  white-space: nowrap;
  letter-spacing: -0.2px;
`;

const SourceTag = styled.span`
  padding: 2px 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: 6px;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MovingDateRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const MovingDateLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MovingDateValue = styled.span`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  gap: 10px;
`;

const MetaLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MetaValue = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
`;

const CardPhoneRow = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 14px 12px;
  background: #22C55E;
  border: 1px solid #16A34A;
  border-radius: ${({ theme }) => theme.radius.sm};
  color: #FFFFFF;
  font-weight: 600;
  text-decoration: none;

  &:hover { background: #16A34A; }

  svg { color: #FFFFFF; }
`;

const CardPhoneText = styled.span`
  font-size: 16px;
  font-weight: 700;
  letter-spacing: 0.3px;
  white-space: nowrap;
`;

const CompleteCtaBtn = styled.button`
  width: 100%;
  padding: 14px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover { opacity: 0.92; }
`;

const PriceChangeHint = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: #FFF7ED;
  border: 1px solid #FED7AA;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 12px;
  color: #9A3412;
  line-height: 1.5;
`;

const CompletedSummary = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const SummaryBox = styled.button`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: 16px;
  background: #fff;
  border: 1.5px solid ${({ $active, $muted, theme }) =>
    $active ? ($muted ? theme.colors.textSecondary : theme.colors.primary) : theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;
  text-align: left;

  &:hover { background: #FAFBFC; }
`;

const SummaryLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const SummaryCount = styled.span`
  font-size: 20px;
  font-weight: 800;
  color: ${({ theme }) => theme.colors.text};
`;

const CardBodyRow = styled.div`
  display: flex;
  gap: 10px;
  background: #F9FAFB;
  border-radius: ${({ theme }) => theme.radius.sm};
  padding: 12px;

  @media (max-width: 640px) {
    flex-direction: column;
    gap: 10px;
  }
`;

const CardBodyLeft = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding-left: 8px;
  border-left: 3px solid ${({ theme }) => theme.colors.primary};
`;

const CardBodyRight = styled.div`
  flex: 1.2;
  min-width: 0;
`;

const ReservedLine = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ReservedDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7C3AED;
  flex: 0 0 auto;
`;

const ReservedLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const ReservedValue = styled.span`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const AmountLine = styled.div`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const BodyPlaceholder = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const MemoBoxInline = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const MemoBoxTitle = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const MemoBoxText = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  word-break: break-all;
`;

const NameRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const Name = styled.span`
  font-size: 17px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
`;

const NewBadge = styled.span`
  padding: 2px 6px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border-radius: 4px;
  font-size: 10px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const MemoHistoryBox = styled.div`
  max-height: 320px;
  min-height: 200px;
  overflow-y: auto;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  background: #F9FAFB;
  padding: 8px;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const MemoHistoryEmpty = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textLight};
  text-align: center;
  padding: 16px 0;
`;

const MemoHistoryItem = styled.div`
  padding: 8px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.sm};
  margin-bottom: 6px;
  &:last-child { margin-bottom: 0; }
`;

const MemoHistoryMeta = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-bottom: 4px;
`;

const MemoHistoryText = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  white-space: pre-wrap;
  word-break: break-all;
`;

const CompletedRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  background: #F9FAFB;
  border-radius: ${({ theme }) => theme.radius.sm};
`;

const CompletedLabel = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CompletedValue = styled.span`
  font-size: 13px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
`;

const ActionGrid = styled.div`
  display: grid;
  grid-template-columns: ${({ $cols }) => `repeat(${$cols || 2}, 1fr)`};
  gap: 8px;
`;

const ActionBtn = styled.button`
  padding: 11px 8px;
  background: #fff;
  color: ${({ theme }) => theme.colors.text};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 14px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #F9FAFB; }
`;

const EmptyCard = styled.div`
  background: #F9FAFB;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: 12px;
  margin-top: 8px;
`;

const EmptyInner = styled.div`
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 40px 20px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  color: ${({ theme }) => theme.colors.textLight};
`;

const EmptyTitle = styled.div`
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-top: 6px;
`;

const EmptySub = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
`;

// 모달 (공통)
const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: ${({ theme }) => theme.spacing.lg};
`;

const ModalBox = styled.div`
  width: 100%;
  max-width: 420px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};
  max-height: 92vh;
  overflow-y: auto;
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const ModalTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

const ModalClose = styled.button`
  display: flex;
  background: none;
  border: 0;
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  padding: 0;
`;

const ModalCustomer = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.md};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;

const ModalLabel = styled.div`
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:first-child { margin-top: 0; }
`;

const StatusGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${({ theme }) => theme.spacing.xs};
  margin-bottom: ${({ theme }) => theme.spacing.sm};
`;

const StatusOptBtn = styled.button`
  padding: ${({ $highlight }) => ($highlight ? '16px 8px' : '10px 8px')};
  border: ${({ $highlight, $active, $color, theme }) =>
    $highlight
      ? `2px solid ${$color}`
      : `1.5px solid ${$active ? $color : theme.colors.border}`};
  background: ${({ $active, $color, $highlight }) =>
    $highlight ? ($active ? $color : `${$color}20`) : $active ? `${$color}15` : '#fff'};
  color: ${({ $active, $color, $highlight, theme }) =>
    $highlight ? ($active ? '#fff' : $color) : $active ? $color : theme.colors.text};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ $highlight }) => ($highlight ? '14px' : '12px')};
  font-weight: ${({ theme, $highlight }) => ($highlight ? 700 : theme.fontWeight.semibold)};
  box-shadow: ${({ $highlight, $color }) => ($highlight ? `0 2px 8px ${$color}40` : 'none')};
  cursor: pointer;
`;

const CancelDivider = styled.div`
  height: 1px;
  background: ${({ theme }) => theme.colors.border};
  margin: ${({ theme }) => theme.spacing.sm} 0;
`;

const ModalInput = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  outline: none;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ModalTextarea = styled.textarea`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-family: inherit;
  outline: none;
  resize: vertical;

  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;

const ModalBtnRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  margin-top: ${({ theme }) => theme.spacing.lg};
`;

const ModalBtn = styled.button`
  flex: 1;
  padding: 12px;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  cursor: pointer;
  border: ${({ $secondary, theme }) => ($secondary ? `1px solid ${theme.colors.border}` : '0')};
  background: ${({ $secondary, theme }) => ($secondary ? '#fff' : theme.colors.primary)};
  color: ${({ $secondary, theme }) => ($secondary ? theme.colors.text : '#fff')};

  &:disabled {
    background: ${({ $secondary, theme }) => ($secondary ? '#fff' : theme.colors.border)};
    color: ${({ theme }) => theme.colors.textLight};
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

// ── 정책/수수료 안내
const PolicyLink = styled.button`
  background: none;
  border: 0;
  padding: 0;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
`;

const PolicyContent = styled.pre`
  white-space: pre-wrap;
  font-family: inherit;
  font-size: 13px;
  line-height: 1.7;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0;
  padding: 16px 0;
`;

const InfoTabRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const InfoTab = styled.button`
  flex: 1;
  padding: 10px 0;
  background: ${({ $active, theme }) => ($active ? `${theme.colors.primary}15` : '#fff')};
  color: ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.textSecondary)};
  border: 1px solid ${({ $active, theme }) => ($active ? theme.colors.primary : theme.colors.border)};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
`;

const InfoSummaryBox = styled.div`
  background: #F9FAFB;
  border-radius: 8px;
  padding: 12px;
  font-size: 12px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 12px;
`;

const InfoHighlight = styled.div`
  background: #EFF6FF;
  border-radius: 8px;
  padding: 12px;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 16px;
`;

const InfoTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 12px;
`;

const InfoTh = styled.th`
  padding: 10px 8px;
  font-size: 12px;
  font-weight: 700;
  background: #F3F4F6;
  color: ${({ theme }) => theme.colors.text};
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
`;

const InfoTd = styled.td`
  padding: 10px 8px;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
`;

const InfoFootnote = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.colors.textLight};
  margin-top: 8px;
`;

// ── 상태 변경 모달 추가 스타일
const ModalSubTitle = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 10px;
`;

const CurrentStatusRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
`;

const CurrentLabel = styled.span`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

const CurrentPill = styled.span`
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.radius.full};
  background: ${({ $bg }) => $bg};
  color: ${({ $color }) => $color};
  font-size: 13px;
  font-weight: 700;
`;

const ModalSectionTitle = styled.h3`
  font-size: 14px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  margin: 14px 0 8px;
`;

const RadioGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`;

const RadioItem = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const AbsentActions = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-top: 10px;
`;

// ── 부재중 안내 문자 템플릿 (신규)
const AbsentBlock = styled.div`
  margin: 14px 0;
`;

const AbsentSectionTitle = styled.h3`
  font-size: 15px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 10px;
`;

const AbsentPhone = styled.div`
  font-size: 13px;
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 10px;

  strong {
    color: ${({ theme }) => theme.colors.text};
    font-weight: 700;
  }
`;

const AbsentSmsBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px;
  background: #22C55E;
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #16A34A; }
`;

const AbsentCopyBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 8px;
  background: #F97316;
  color: #fff;
  border: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  white-space: nowrap;

  &:hover { background: #EA580C; }
`;

const AbsentHelpText = styled.p`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.6;
  margin: 10px 0 0;
`;

const CardFeeLink = styled.button`
  margin-top: 8px;
  background: none;
  border: 0;
  padding: 4px;
  font-size: 12px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: underline;
  cursor: pointer;
  align-self: center;
`;
